import type { PreparedTrack, TrackAudioProfile, VisualStyle } from '../types';
import { DEFAULT_BPM } from '../types';
import { detectHooks } from './hookDetection';
import { fetchSyncedLyrics } from './lyricsApi';
import { extractPaletteFromImage, applyVisualStyle } from './palette';
import { estimateSections } from './sections';
import { buildVisualTimeline } from './timeline';
import { hashString } from './hash';
import { FALLBACK_AUDIO_PROFILE, intensityFromAudioProfile } from './spotifyAudioProfile';

export interface TrackInput {
  id: string;
  title: string;
  artist: string;
  album: string;
  durationMs: number;
  albumArtUrl: string;
  bpm?: number;
  audioProfile?: TrackAudioProfile;
}

const cache = new Map<string, PreparedTrack>();
const inflight = new Map<string, Promise<PreparedTrack>>();
const MAX_TRACK_CACHE_ENTRIES = 96;

function trimTrackCache() {
  while (cache.size > MAX_TRACK_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (!oldestKey) break;
    cache.delete(oldestKey);
  }
}

export function getCachedTrack(trackId: string): PreparedTrack | undefined {
  return cache.get(trackId);
}

export function getCacheSize(): number {
  return cache.size;
}

export async function prepareTrack(
  input: TrackInput,
  style: VisualStyle = 'festival',
  options: { lazy?: boolean } = {}
): Promise<PreparedTrack> {
  const inputProfileKey = input.audioProfile
    ? `${input.audioProfile.bpmSource}:${input.audioProfile.bpm}:${input.audioProfile.beatOffsetMs}`
    : `bpm:${input.bpm ?? DEFAULT_BPM}`;
  const cacheKey = `${input.id}:${style}:${inputProfileKey}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const pending = inflight.get(cacheKey);
  if (pending) return pending;

  const promise = (async () => {
    const seed = hashString(input.id);
    const lyricsRaw = options.lazy
      ? []
      : await fetchSyncedLyrics(input.title, input.artist, input.durationMs);
    const lyrics = detectHooks(lyricsRaw, input.durationMs);
    const audioProfile: TrackAudioProfile = input.audioProfile
      ? input.audioProfile
      : {
          ...FALLBACK_AUDIO_PROFILE,
          bpm: input.bpm && input.bpm > 0 ? input.bpm : DEFAULT_BPM,
          bpmSource: input.bpm && input.bpm > 0 ? 'manual' : 'fallback',
          confidence: input.bpm && input.bpm > 0 ? 0.8 : FALLBACK_AUDIO_PROFILE.confidence,
        };
    const bpm = audioProfile.bpm;
    const paletteBase = await extractPaletteFromImage(input.albumArtUrl, seed);
    const palette = applyVisualStyle(paletteBase, style);
    const sections = estimateSections(input.durationMs, lyrics, bpm);
    const visualTimeline = buildVisualTimeline(
      input.id,
      input.durationMs,
      sections,
      style,
      intensityFromAudioProfile(audioProfile)
    );
    const hooks = lyrics.filter((l) => l.isLikelyHook);

    const prepared: PreparedTrack = {
      spotifyTrackId: input.id,
      title: input.title,
      artist: input.artist,
      album: input.album,
      durationMs: input.durationMs,
      albumArtUrl: input.albumArtUrl,
      bpm,
      palette,
      lyrics,
      visualTimeline,
      hooks,
      preparedAt: Date.now(),
      hasLyrics: lyrics.length > 0,
      audioProfile,
    };

    cache.set(cacheKey, prepared);
    cache.set(input.id, prepared);
    trimTrackCache();
    return prepared;
  })().finally(() => {
    inflight.delete(cacheKey);
  });

  inflight.set(cacheKey, promise);
  return promise;
}

export async function enrichTrackLyrics(track: PreparedTrack): Promise<PreparedTrack> {
  if (track.hasLyrics) return track;
  const lyricsRaw = await fetchSyncedLyrics(track.title, track.artist, track.durationMs);
  const lyrics = detectHooks(lyricsRaw, track.durationMs);
  const sections = estimateSections(track.durationMs, lyrics, track.bpm);
  const visualTimeline = buildVisualTimeline(
    track.spotifyTrackId,
    track.durationMs,
    sections,
    'festival',
    intensityFromAudioProfile(track.audioProfile)
  );
  const updated = {
    ...track,
    lyrics,
    hooks: lyrics.filter((l) => l.isLikelyHook),
    visualTimeline,
    hasLyrics: lyrics.length > 0,
    preparedAt: Date.now(),
  };
  cache.set(track.spotifyTrackId, updated);
  trimTrackCache();
  return updated;
}

export function clearTrackCache(): void {
  cache.clear();
  inflight.clear();
}
